import pictogramAsset from "assets/img/pictogram/asset.svg"
import IconPlannedReleased from "assets/img/icon-so-planned-released.svg?react"
import IconPlannedStored from "assets/img/icon-so-planned-stored.svg?react"
import IconPlannedReplaced from "assets/img/icon-so-planned-replaced.svg?react"
import IconUnplannedReleased from "assets/img/icon-so-unplanned-released.svg?react"
import IconUnplannedStored from "assets/img/icon-so-unplanned-stored.svg?react"
import IconPlannedNotReleased from "assets/img/icon-so-planned-not-released.svg?react"
import IconPlannedNotStored from "assets/img/icon-so-planned-not-stored.svg?react"

import { FC, ReactNode, useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"

import Table, { TableColumn, TableRow } from "components/Table/Table"
import { Grid } from "components/Grid"
import Pictogram from "components/Pictogram/Pictogram"
import FlexibleContent from "components/FlexibleContent/FlexibleContent"
import Lmid from "components/Lmid/Lmid"
import { Guid } from "@/types"
import articleSidebars from "stores/articleSidebars"
import ShippingOrderAssetExtended from "models/ShippingOrderAssetExtended"
import { IShippingResultAssetsSort, ShippingResultAssetsSortBy } from "./config"
import { ShippingOrderType } from "models/ShippingOrder"
import { Flex, FlexItem } from "components/Flex"
import { orderBy } from "lodash"

enum ShippingResultOperation {
    plannedReleased = "plannedReleased",
    plannedStored = "plannedStored",
    plannedReplaced = "plannedReplaced",
    unplannedReleased = "unplannedReleased",
    unplannedStored = "unplannedStored",
    plannedNotReleased = "plannedNotReleased",
    plannedNotStored = "plannedNotStored",
}

interface IShippingResultAsset {
    articleId: Guid
    articleDescription: string
    altArticleDescription?: string
    assetId: Guid
    lmid: string
    isLabeled: boolean
}

interface IShippingResultItem {
    planned: IShippingResultAsset | null
    shipped: IShippingResultAsset | null
    operation: ShippingResultOperation | null
}

interface IShippingOrderModalShippingResultProps {
    shippingOrderType: ShippingOrderType
    sort: IShippingResultAssetsSort
    setSort: (sort: IShippingResultAssetsSort) => void
    shippedAssets: ShippingOrderAssetExtended[]
}

const ShippingOrderModalShippingResult: FC<IShippingOrderModalShippingResultProps> = ({
    shippingOrderType,
    sort,
    setSort,
    shippedAssets,
}) => {
    const { t } = useTranslation()

    const sortShippingResultItems = useCallback((items: IShippingResultItem[]) => {
        switch(sort.sortBy) {
            case ShippingResultAssetsSortBy.plannedArticleDescription:
                return orderBy(items, item => [item.planned?.articleDescription, item.planned?.lmid], sort.sortDirection)
            case ShippingResultAssetsSortBy.plannedLmid:
                return orderBy(items, item => item.planned?.lmid, sort.sortDirection)
            case ShippingResultAssetsSortBy.operation:
                return orderBy(items, item => [item.operation ? Object.values(ShippingResultOperation).indexOf(item.operation) : null, item.planned?.articleDescription, item?.planned?.lmid, item.shipped?.articleDescription, item.shipped?.lmid], sort.sortDirection)
            case ShippingResultAssetsSortBy.shippedArticleDescription:
                return orderBy(items, item => [item.shipped?.articleDescription, item.shipped?.lmid], sort.sortDirection)
            case ShippingResultAssetsSortBy.shippedLmid:
                return orderBy(items, item => item.shipped?.lmid, sort.sortDirection)
            default:
                return items
        }
    }, [sort.sortBy, sort.sortDirection])

    const shippingResultItems: IShippingResultItem[] = useMemo(() => {
        const items: IShippingResultItem[] = shippedAssets.map(asset => {
            let plannedAsset: IShippingResultAsset | null = null
            if (asset.plannedAsset) {
                plannedAsset = {
                    articleId: asset.plannedAsset.articleId,
                    articleDescription: asset.plannedAsset.articleDescription || "",
                    altArticleDescription: asset.plannedAsset.altArticleDescription,
                    assetId: asset.plannedAsset.assetId,
                    lmid: asset.plannedAsset.lmid,
                    isLabeled: asset.plannedAsset.isLabeled,
                }
            }

            let shippedAsset: IShippingResultAsset | null = null
            if ((shippingOrderType === ShippingOrderType.delivery && asset.releaseDate)
                || (shippingOrderType === ShippingOrderType.return && asset.storeDate)) {
                shippedAsset = {
                    articleId: asset.articleId,
                    articleDescription: asset.articleDescription,
                    altArticleDescription: asset.altArticleDescription,
                    assetId: asset.assetId,
                    lmid: asset.lmid,
                    isLabeled: asset.isLabeled,
                }
            }

            let operation: ShippingResultOperation | null = null
            if (plannedAsset && shippedAsset) {
                operation = plannedAsset.assetId === shippedAsset.assetId
                    ? (shippingOrderType === ShippingOrderType.delivery ? ShippingResultOperation.plannedReleased : ShippingResultOperation.plannedStored)
                    : ShippingResultOperation.plannedReplaced
            } else if (plannedAsset && !shippedAsset) {
                operation = shippingOrderType === ShippingOrderType.delivery ? ShippingResultOperation.plannedNotReleased : ShippingResultOperation.plannedNotStored
            } else if (!plannedAsset && shippedAsset) {
                operation = shippingOrderType === ShippingOrderType.delivery ? ShippingResultOperation.unplannedReleased : ShippingResultOperation.unplannedStored
            }

            return {
                planned: plannedAsset,
                shipped: shippedAsset,
                operation,
            }
        })
        return sortShippingResultItems(items)
    }, [shippedAssets, shippingOrderType, sortShippingResultItems])

    const renderOperation = useCallback((operation: ShippingResultOperation | null) => {
        let icon: ReactNode | null = null
        let label: string | null = null

        switch (operation) {
            case ShippingResultOperation.plannedReleased:
                icon = <IconPlannedReleased />
                label = t("shippingOrders.operationType.released")
                break
            case ShippingResultOperation.plannedStored:
                icon = <IconPlannedStored />
                label = t("shippingOrders.operationType.stored")
                break
            case ShippingResultOperation.plannedReplaced:
                icon = <IconPlannedReplaced />
                label = t("shippingOrders.operationType.replacedBy")
                break
            case ShippingResultOperation.unplannedReleased:
                icon = <IconUnplannedReleased />
                label = t("shippingOrders.operationType.added")
                break
            case ShippingResultOperation.unplannedStored:
                icon = <IconUnplannedStored />
                label = t("shippingOrders.operationType.stored")
                break
            case ShippingResultOperation.plannedNotReleased:
                icon = <IconPlannedNotReleased />
                label = t("shippingOrders.operationType.notReleased")
                break
            case ShippingResultOperation.plannedNotStored:
                icon = <IconPlannedNotStored />
                label = t("shippingOrders.operationType.notStored")
        }

        if (!icon && !label) {
            return "–"
        }

        return (
            <Flex alignItems="center" gap="0.8rem">
                {!!icon &&
                    <FlexItem shrink={0}>
                        {icon}
                    </FlexItem>
                }
                {!!label &&
                    <FlexibleContent text={label} />
                }
            </Flex>
        )
    }, [t])

    const prependHeaderRow: TableColumn[] = useMemo(() => ([
        {
            title: t("shippingOrders.planned"),
            width: "42%",
            colSpan: 2,
            headerClassName: "pt-half pb-0 color-grey-600",
        },
        {
            title: "",
            width: "16%",
            headerClassName: "pt-half pb-0 color-grey-600 border-left-grey-200 border-right-grey-200",
        },
        {
            title: t("shippingOrders.shipped"),
            width: "42%",
            colSpan: 2,
            headerClassName: "pt-half pb-0 color-grey-600",
        },
    ]), [t])

    const tableColumns: TableColumn[] = useMemo(() => ([
        {
            title: t("common.article"),
            width: "27%",
            className: "pt-half pb-1",
            sort: sort.sortBy === ShippingResultAssetsSortBy.plannedArticleDescription ? sort.sortDirection : undefined,
            onSortChange: sortDirection => setSort({ sortBy: ShippingResultAssetsSortBy.plannedArticleDescription, sortDirection }),
        },
        {
            title: t("common.lmid"),
            width: "15%",
            className: "pt-half pb-1",
            sort: sort.sortBy === ShippingResultAssetsSortBy.plannedLmid ? sort.sortDirection : undefined,
            onSortChange: sortDirection => setSort({ sortBy: ShippingResultAssetsSortBy.plannedLmid, sortDirection }),
        },
        {
            title: t("shippingOrders.operation"),
            width: "16%",
            className: "pt-half pb-1 border-left-grey-100 border-right-grey-100",
            headerClassName: "border-left-grey-200 border-right-grey-200",
            sort: sort.sortBy === ShippingResultAssetsSortBy.operation ? sort.sortDirection : undefined,
            onSortChange: sortDirection => setSort({ sortBy: ShippingResultAssetsSortBy.operation, sortDirection }),
        },
        {
            title: t("common.article"),
            width: "27%",
            className: "pt-half pb-1",
            sort: sort.sortBy === ShippingResultAssetsSortBy.shippedArticleDescription ? sort.sortDirection : undefined,
            onSortChange: sortDirection => setSort({ sortBy: ShippingResultAssetsSortBy.shippedArticleDescription, sortDirection }),
        },
        {
            title: t("common.lmid"),
            width: "15%",
            className: "pt-half pb-1",
            sort: sort.sortBy === ShippingResultAssetsSortBy.shippedLmid ? sort.sortDirection : undefined,
            onSortChange: sortDirection => setSort({ sortBy: ShippingResultAssetsSortBy.shippedLmid, sortDirection }),
        },
    ]), [setSort, sort.sortBy, sort.sortDirection, t])

    const tableRows = useMemo<TableRow[]>(() => shippingResultItems.map(item => ([
        item.planned
            ? <button type="button"
                onClick={() => articleSidebars.show({ articleId: item.planned?.articleId })}
                className="link d-block color-brand-blue">
                <Grid columns="max-content minmax(0, auto)"
                    gap="1.6rem"
                    alignItems="center">
                    <Pictogram
                        src={pictogramAsset}
                        size="4rem"
                        imgSize="2.2rem"
                    />
                    <FlexibleContent text={item.planned.altArticleDescription || item.planned.articleDescription} />
                </Grid>
            </button>
            : "–",
        item.planned
            ? <Lmid
                lmid={item.planned.lmid}
                assetId={item.planned.assetId}
                labeled={item.planned.isLabeled}
            />
            : "–",
        renderOperation(item.operation),
        item.shipped
            ? <button type="button"
                onClick={() => articleSidebars.show({ articleId: item.shipped?.articleId })}
                className="link d-block color-brand-blue">
                <Grid columns="max-content minmax(0, auto)"
                    gap="1.6rem"
                    alignItems="center">
                    <Pictogram
                        src={pictogramAsset}
                        size="4rem"
                        imgSize="2.2rem"
                    />
                    <FlexibleContent text={item.shipped.altArticleDescription || item.shipped.articleDescription} />
                </Grid>
            </button>
            : "–",
        item.shipped
            ? <Lmid
                lmid={item.shipped.lmid}
                assetId={item.shipped.assetId}
                labeled={item.shipped.isLabeled}
            />
            : "–",
    ])), [renderOperation, shippingResultItems])

    return (
        <>
            <Table
                prependHeaderRow={prependHeaderRow}
                columns={tableColumns}
                rows={tableRows}
                theme="border-between"
                stickyHeader
                condensed
            />
        </>
    )
}

export default ShippingOrderModalShippingResult
